// src/GlobalLogic.js
import API_BASE_URL from '../config';

export const GLOBAL_CALCULATIONS = {
    // --- CENTRALIZED DATA STORAGE ---
    allOrders: [],
    allArticles: [],

    // --- DEDUPE TRACKER ---
    // Holds the in-flight/completed fetch promise so repeated calls
    // (from multiple components/mounts) don't trigger duplicate requests.
    _initPromise: null,

    // --- FETCH & STORE FUNCTION ---
    // Safe to call from any component/mount; the actual network fetch
    // only happens once until refreshData() is called.
    initializeData: async () => {
        if (GLOBAL_CALCULATIONS._initPromise) {
            return GLOBAL_CALCULATIONS._initPromise;
        }

        GLOBAL_CALCULATIONS._initPromise = (async () => {
            try {
                const [orderRes, artRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/ordershowa`),
                    fetch(`${API_BASE_URL}/articleshowa`)
                ]);
                GLOBAL_CALCULATIONS.allOrders = await orderRes.json();
                GLOBAL_CALCULATIONS.allArticles = await artRes.json();
                console.log("Global Data Initialized");
            } catch (error) {
                console.error("Global Fetch Error:", error);
                // Allow retry on next call if it failed
                GLOBAL_CALCULATIONS._initPromise = null;
            }
        })();

        return GLOBAL_CALCULATIONS._initPromise;
    },

    // --- FORCE A FRESH FETCH (use after add/edit/delete) ---
    refreshData: async () => {
        GLOBAL_CALCULATIONS._initPromise = null;
        return GLOBAL_CALCULATIONS.initializeData();
    },

    // --- UTILITY TO GET DATA BY ID ---
    // Use this in your tables: GLOBAL_CALCULATIONS.getOrderByID(id).sellingprice
    getOrderByID: (id) => {
        return GLOBAL_CALCULATIONS.allOrders.find(o => o._id === id) || {};
    },

    getArticleByName: (name) => {
        return GLOBAL_CALCULATIONS.allArticles.find(a => a.name === name) || {};
    },

    // Calculate total money still due from all customers
    getTotalPendingFromCustomers: () => {
        return GLOBAL_CALCULATIONS.allOrders.reduce((total, order) => {
            const price = Number(order.sellingprice) || 0;
            const received = Number(order.payrec) || 0;
            const balance = price - received;
            
            // Only add to total if there is actually money pending
            return balance > 0 ? total + balance : total;
        }, 0);
    },

    // --- CALCULATE AVERAGES BY ARTICLE NAME ---
    getArticleAverages: (articleName) => {
        // 1. Ensure allOrders exists
        const orders = GLOBAL_CALCULATIONS.allOrders || [];
        
        // 2. Defensive check: if no name is passed, don't filter
        if (!articleName) return { avgSellingPrice: 0, avgShipping: 0, avgExtraCost: 0 };

        // 3. Robust Filtering
        const relatedOrders = orders.filter(o => {
            // We convert to String and use trim() to remove hidden spaces
            const orderArtName = String(o.articlename || "").trim();
            const searchArtName = String(articleName).trim();
            
            return orderArtName === searchArtName;
        });

        const count = relatedOrders.length;
        
        // Console logs for debugging
        // console.log("Target Name:", `"${articleName}"`);
        // console.log("Matched Count:", count);

        if (count === 0) return { avgSellingPrice: 0, avgShipping: 0, avgExtraCost: 0 };

        const totals = relatedOrders.reduce((acc, order) => {
            return {
                selling: acc.selling + (Number(order.sellingprice) || 0),
                shipping: acc.shipping + (Number(order.shipping) || 0),
                extra: acc.extra + (Number(order.extracost) || 0)
            };
        }, { selling: 0, shipping: 0, extra: 0 });

        return {
            avgSellingPrice: Math.round(totals.selling / count),
            avgShipping: Math.round(totals.shipping / count),
            avgExtraCost: Math.round(totals.extra / count)
        };
    },

    // --- YOUR EXISTING LOGIC (UNCHANGED) ---
    getDateFromDays: (days) => {
        if (!days || days < 0) return "";
        const date = new Date();
        date.setDate(date.getDate() + Number(days));
        return date.toISOString().split('T')[0];
    },

    getDaysFromDate: (targetDate) => {
        if (!targetDate) return "";
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const delivery = new Date(targetDate);
        delivery.setHours(0, 0, 0, 0);
        const diffInMs = delivery - today;
        const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
        return diffInDays >= 0 ? diffInDays : 0;
    },

    getArticleProductionPrice: (article) => {
        if (!article) return 0;
        return (Number(article.fabric) || 0) + 
               (Number(article.work) || 0) + 
               (Number(article.stiching) || 0) + 
               (Number(article.misc) || 0) +
               (Number(article.dupatta) || 0);
    },

    getOrderInternalCost: (o, article) => {
        // const productionPrice = GLOBAL_CALCULATIONS.getArticleProductionPrice(article);
        const productionPrice = 
        (Number(o.fabric) || 0) +
        (Number(o.fabricdye) || 0) + 
        (Number(o.work) || 0) + 
        (Number(o.stitching) || 0) + 
        (Number(o.lases) || 0) + 
        (Number(o.dupatta) || 0) +
        (Number(o.dupattadye) || 0) +
        (Number(o.fabricdispatch) || 0) +
        (Number(o.unstitcheddispatch) || 0) +
        (Number(o.fabricsourcing) || 0) +
        (Number(o.stitcheddispatch) || 0) +
        (Number(o.packagingcost) || 0) + 
        (Number(o.packagedispatched) || 0) + 
        (Number(o.courierfee) || 0) + 
        (Number(o.extracost) || 0);

    // 2. Shipping Expenses (Money leaving your pocket)
    // We sum up the actual costs paid to Karigar, Courier, etc.
    const shippingExpenses = o.shipping_details ? (
        (Number(o.shipping_details.to_karigar) || 0) +
        (Number(o.shipping_details.to_sufna) || 0) +
        (Number(o.shipping_details.to_courier) || 0) +
        (Number(o.shipping_details.to_customer) || 0)
    ) : 0;
        // console.log(extra);
        return productionPrice + shippingExpenses;
    },

    getRemainingDaysStatus: (deliveryDate, currentStatus) => {
        if (currentStatus === "Delivered") return { text: "Delivered", color: "#00FF00" }; 
        if (!deliveryDate) return { text: "No Date", color: "gray" };

        const today = new Date().setHours(0, 0, 0, 0);
        const delivery = new Date(deliveryDate).setHours(0, 0, 0, 0);
        const diff = Math.ceil((delivery - today) / (1000 * 60 * 60 * 24));

        if (diff < 0) return { text: `${Math.abs(diff)} Days Overdue`, color: "#8B0000" }; 

        switch (diff) {
            case 0: return { text: "Due Today", color: "#CC0000" };
            case 1: return { text: "1 Day Left", color: "#FF0000" };
            case 2: return { text: "2 Days Left", color: "#FF4500" };
            case 3: return { text: "3 Days Left", color: "#FF8C00" };
            case 4: return { text: "4 Days Left", color: "#FFA500" };
            case 5: return { text: "5 Days Left", color: "#FFD700" };
            default: return { text: `${diff} Days Left`, color: "black" };
        }
    }
};